import {useEffect, useState, type ReactNode} from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth'
import * as Sentry from '@sentry/react'
import {auth, googleProvider} from './firebase'
import {fetchMe, resetMeCache} from '../../common/hooks/useMe'
import {isSentryEnabled} from '../../common/errors/sentry'
import {addBreadcrumb} from '../../common/errors/breadcrumb'
import {setGaUserId, setGaUserProperties} from '../../common/analytics/ga'
import {trackEvent} from '../../common/analytics/trackEvent'
import {AuthContext} from './authTypes'

const EMAIL_STORAGE_KEY = 'emailForMagicLink'

export const AuthProvider = ({children}: {children: ReactNode}) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        // uid only — no email (PII)
        if (isSentryEnabled()) Sentry.setUser({id: firebaseUser.uid})
        setGaUserId(firebaseUser.uid)
        setGaUserProperties({signed_in: true})
        addBreadcrumb('auth', 'state-signed-in', 'info', {uid: firebaseUser.uid})
        // Upsert user record in the database (also primes the useMe() cache)
        try {
          await fetchMe()
        } catch {
          // Silent fail — DB sync is best-effort on auth state change
        }
      } else {
        if (isSentryEnabled()) Sentry.setUser(null)
        setGaUserId(null)
        setGaUserProperties({signed_in: false})
        addBreadcrumb('auth', 'state-signed-out', 'info')
        resetMeCache()
      }
      setLoading(false)
    })

    const handleAuthExpired = () => {
      firebaseSignOut(auth).catch(() => {})
    }
    window.addEventListener('app:auth-expired', handleAuthExpired)

    return () => {
      unsubscribe()
      window.removeEventListener('app:auth-expired', handleAuthExpired)
    }
  }, [])

  const signInWithGoogle = async () => {
    addBreadcrumb('auth', 'google-signin-start', 'info')
    await signInWithPopup(auth, googleProvider)
    trackEvent('signin_completed', {provider: 'google'})
  }

  const sendMagicLink = async (email: string) => {
    addBreadcrumb('auth', 'magic-link-send', 'info')
    const actionCodeSettings = {
      url: `${window.location.origin}/login/verify`,
      handleCodeInApp: true,
    }
    await sendSignInLinkToEmail(auth, email, actionCodeSettings)
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email)
  }

  const completeMagicLinkSignIn = async (url: string, emailFallback?: string) => {
    addBreadcrumb('auth', 'magic-link-complete', 'info')
    if (!isSignInWithEmailLink(auth, url)) {
      throw new Error('Invalid sign-in link')
    }
    const stored = window.localStorage.getItem(EMAIL_STORAGE_KEY)
    const email = stored ?? emailFallback
    if (!email) {
      throw new Error('Email required to complete sign-in')
    }
    await signInWithEmailLink(auth, email, url)
    window.localStorage.removeItem(EMAIL_STORAGE_KEY)
    trackEvent('magic_link_completed')
    trackEvent('signin_completed', {provider: 'magic_link'})
  }

  const signOut = async () => {
    addBreadcrumb('auth', 'signout', 'info')
    await firebaseSignOut(auth)
    resetMeCache()
  }

  return (
    <AuthContext value={{user, loading, signInWithGoogle, sendMagicLink, completeMagicLinkSignIn, signOut}}>
      {children}
    </AuthContext>
  )
}
